import { useState, useEffect, useRef } from "react";
import { useTheme } from "@/hooks/useTheme";
import { navLinks } from "@/mocks/homeContent";

export default function Navbar() {
  const { isDark, toggle } = useTheme();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const headerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavClick = (href: string) => {
    setMenuOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header ref={headerRef} className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? "bg-background-50/95 backdrop-blur-xl border-b border-background-200/60" : "bg-transparent"}`}>
      <nav className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-12 h-16 md:h-20 flex items-center justify-between">
        <a
          href="#hero"
          onClick={(e) => {
            e.preventDefault();
            handleNavClick("#hero");
          }}
          className="flex items-center gap-2 cursor-pointer"
        >
          <span className="text-xl md:text-2xl font-bold text-foreground-950 tracking-tight">
                Cybi<span className="text-accent-500">.</span>Solutions
          </span>
          {/* <img src="/logo/logo.webp" alt="Cybi Solutions" className="h-12 w-auto" /> */}
        </a>



        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => {
                e.preventDefault();
                handleNavClick(link.href);
              }}
              className="text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              {link.label}
            </a>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <button onClick={toggle} className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 hover:bg-background-200 transition-colors duration-200 cursor-pointer" aria-label="Toggle dark mode">
            {isDark ? (
              <i className="ri-sun-line text-foreground-700 text-lg w-5 h-5 flex items-center justify-center transition-transform duration-300" />
            ) : (
              <i className="ri-moon-line text-foreground-700 text-lg w-5 h-5 flex items-center justify-center transition-transform duration-300" />
            )}
          </button>

          <a
            href="#cta"
            onClick={(e) => {
              e.preventDefault();
              handleNavClick("#cta");
            }}
            className="hidden md:inline-flex items-center px-5 py-2.5 rounded-full bg-primary-500 text-white text-sm font-semibold hover:bg-primary-400 transition-colors duration-200 whitespace-nowrap cursor-pointer"
          >
            Free Consultation
          </a>

          <button onClick={() => setMenuOpen(!menuOpen)} className="md:hidden w-9 h-9 flex items-center justify-center rounded-full bg-background-100 hover:bg-background-200 transition-colors duration-200 cursor-pointer" aria-label="Toggle menu">
            <i className={`text-foreground-700 text-lg w-5 h-5 flex items-center justify-center ${menuOpen ? "ri-close-line" : "ri-menu-line"}`} />
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      <div className={`md:hidden overflow-hidden transition-all duration-300 ${menuOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"}`}>
        <div className="bg-background-50/98 backdrop-blur-xl border-t border-background-200/60 px-4 py-4 space-y-1">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => {
                e.preventDefault();
                handleNavClick(link.href);
              }}
              className="block px-4 py-3 text-sm font-medium text-foreground-700 hover:text-foreground-950 hover:bg-background-100 rounded-lg transition-colors duration-200"
            >
              {link.label}
            </a>
          ))}
          <a
            href="#cta"
            onClick={(e) => {
              e.preventDefault();
              handleNavClick("#cta");
            }}
            className="block text-center mx-4 mt-3 px-5 py-3 rounded-full bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors duration-200 cursor-pointer"
          >
            Free Consultation
          </a>
        </div>
      </div>
    </header>
  );
}
